import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PieChartComponent from '../components/charts/PieChartComponent';
import AreaChartComponent from '../components/charts/AreaChartComponent';
import BarChartComponent from '../components/charts/BarChartComponent';
import RadarChartComponent from '../components/charts/RadarChartComponent';

const BACKEND = "https://painscout.onrender.com";
const VIBE_BACKEND = "https://painscout.onrender.com";

const CATEGORY_COLORS = {
  'Finance': '#22d3ee', 'Mental Health': '#a78bfa', 'Career': '#fb923c',
  'Health': '#4ade80', 'Tech': '#60a5fa', 'Relationships': '#f472b6',
  'Education': '#facc15', 'Home & Life': '#a3e635', 'Other': '#7b7a88',
};

// ── Reusable modal ────────────────────────────────────────────────────────────
const Modal = ({ isOpen, onClose, title, children }) => {
  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.85)' }}
      onClick={onClose}>
      <div className="bg-[#17171c] border border-[#22222e] rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#22222e] sticky top-0 bg-[#17171c] z-10">
          <h3 className="font-bold text-lg" style={{ fontFamily: 'Syne, sans-serif' }}>{title}</h3>
          <button onClick={onClose} className="text-[#7b7a88] hover:text-white transition-colors text-2xl leading-none">×</button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

// ── Small "View" pill button ──────────────────────────────────────────────────
const ViewBtn = ({ onClick }) => (
  <button onClick={onClick}
    className="ml-auto flex-shrink-0 px-3 py-1 rounded-full text-xs font-bold border border-[#ff4500]/40 text-[#ff4500] hover:bg-[#ff4500]/10 transition-all"
    style={{ fontFamily: 'DM Mono, monospace' }}>
    VIEW
  </button>
);

// ── truncate helper ───────────────────────────────────────────────────────────
const trunc = (str, n = 120) => str && str.length > n ? str.slice(0, n) + '…' : (str || '—');

// ── Generating overlay ────────────────────────────────────────────────────────
const GeneratingOverlay = ({ solutionTitle }) => (
  <div className="fixed inset-0 z-[200] flex items-center justify-center"
    style={{ background: 'rgba(9,9,14,0.95)' }}>
    <div className="text-center space-y-6 max-w-md px-8">
      {/* Animated ring */}
      <div className="relative w-24 h-24 mx-auto">
        <svg className="w-24 h-24 animate-spin" viewBox="0 0 96 96">
          <circle cx="48" cy="48" r="40" stroke="#22222e" strokeWidth="6" fill="none" />
          <circle cx="48" cy="48" r="40" stroke="#ff4500" strokeWidth="6" fill="none"
            strokeDasharray="60 191" strokeLinecap="round" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center text-2xl">🤖</div>
      </div>

      <div>
        <p className="text-[#ff4500] text-xs font-bold mb-2 tracking-widest" style={{ fontFamily: 'DM Mono, monospace' }}>
          NOVA IS THINKING
        </p>
        <h3 className="text-white text-xl font-bold mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
          Crafting your build prompt
        </h3>
        <p className="text-[#7b7a88] text-sm" style={{ fontFamily: 'DM Mono, monospace' }}>
          {trunc(solutionTitle, 60)}
        </p>
      </div>

      <div className="flex justify-center gap-1.5">
        {[0, 1, 2, 3, 4].map(i => (
          <div key={i} className="w-1.5 h-1.5 rounded-full bg-[#ff4500]"
            style={{ animation: `bounce 1.2s ease-in-out ${i * 0.15}s infinite` }} />
        ))}
      </div>

      <p className="text-[#3a3a48] text-xs" style={{ fontFamily: 'DM Mono, monospace' }}>
        Analyzing market data · Generating architecture · Crafting prompt
      </p>
    </div>
    <style>{`@keyframes bounce { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }`}</style>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────

const ProblemDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [problem,   setProblem]   = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [scoreProgress, setScoreProgress] = useState(0);

  // Vibe coding state
  const [generatingPrompt, setGeneratingPrompt] = useState(false);
  const [generatingSolution, setGeneratingSolution] = useState(null); // title of solution being generated

  // Modal state
  const [modal, setModal] = useState(null);

  const openModal  = (key) => setModal(key);
  const closeModal = ()    => setModal(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch(`${BACKEND}/api/problems/${id}`).then(r => { if (!r.ok) throw new Error(`${r.status}`); return r.json(); }),
      fetch(`${BACKEND}/api/problems/${id}/analysis`).then(r => { if (!r.ok) throw new Error(`${r.status}`); return r.json(); }),
    ])
      .then(([card, dash]) => {
        setProblem(card);
        setDashboard(dash);
        setLoading(false);
        const target = dash.opportunity_score || card.nova?.opportunity_score || 0;
        setTimeout(() => {
          let p = 0;
          const iv = setInterval(() => {
            p += 2; setScoreProgress(p);
            if (p >= target) { clearInterval(iv); setScoreProgress(target); }
          }, 20);
        }, 300);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [id]);

  // ── Handle solution click → generate prompt → navigate ───────────────────
  const handleBuildSolution = async (solution) => {
    if (!problem || !dashboard) return;

    setGeneratingSolution(solution.title);
    setGeneratingPrompt(true);

    try {
      const d = dashboard || {};
      const competitors = (d.competitors || []).map(c => c.name).filter(Boolean);

      const res = await fetch(`${VIBE_BACKEND}/api/vibe/generate-prompt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          solution_title:    solution.title,
          solution_type:     solution.type || 'App Idea',
          problem_summary:   problem.nova?.problem_summary || '',
          affected_audience: problem.nova?.affected_audience || '',
          pain_points:       problem.nova?.key_pain_points || [],
          category:          problem.reddit?.category || 'Other',
          severity:          problem.nova?.severity || 'Medium',
          competitors:       competitors.slice(0, 3),
          market_size:       d.market_size?.tam || '',
          recommendation:    d.recommendation || {},
        }),
      });

      if (!res.ok) throw new Error(`Prompt generation failed: ${res.status}`);
      const data = await res.json();

      // Navigate to vibe coding window with all context
      navigate(`/build/${id}`, {
        state: {
          solution: {
            ...solution,
            name: solution.title,
            sessionId: data.session_id,
          },
          problem: {
            ...problem,
            reddit: problem.reddit,
            nova: problem.nova,
          },
          generatedPrompt: data.prompt,
          sessionId: data.session_id,
          vibeBackend: VIBE_BACKEND,
        }
      });

    } catch (err) {
      console.error('Prompt generation error:', err);
      // Fall back to navigating without pre-generated prompt
      navigate(`/build/${id}`, {
        state: {
          solution: { ...solution, name: solution.title },
          problem: {
            ...problem,
            reddit: problem.reddit,
            nova: problem.nova,
          },
          generatedPrompt: `Build a ${solution.title} app that solves: ${problem.nova?.problem_summary || ''}`,
          vibeBackend: VIBE_BACKEND,
        }
      });
    } finally {
      setGeneratingPrompt(false);
      setGeneratingSolution(null);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#09090e] flex items-center justify-center gap-3 text-[#7b7a88]" style={{ fontFamily: 'DM Mono, monospace' }}>
      {[0, 150, 300].map(d => <div key={d} className="w-2 h-2 bg-[#ff4500] rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }} />)}
      <span className="ml-2">Loading analysis...</span>
    </div>
  );

  if (error || !problem) return (
    <div className="min-h-screen bg-[#09090e] flex flex-col items-center justify-center gap-4 text-[#7b7a88]" style={{ fontFamily: 'DM Mono, monospace' }}>
      <div className="text-[#ef4444]">❌ {error || 'Problem not found'}</div>
      <button onClick={() => navigate('/dashboard')} className="text-[#ff4500] hover:underline">← Back</button>
    </div>
  );

  const d = dashboard || {};
  const categoryColor = CATEGORY_COLORS[problem.reddit.category] || '#7b7a88';

  const market = {
    tam:              d.market_size?.tam              || '—',
    sam:              d.market_size?.sam              || '—',
    som:              d.market_size?.som              || '—',
    cagr:             d.market_size?.cagr             || '—',
    postsDay:         d.reddit_signal?.posts_last_24h || problem.reddit.score,
    avgUpvotes:       d.reddit_signal?.avg_upvotes    || problem.reddit.score,
    score:            d.opportunity_score             || problem.nova?.opportunity_score || 0,
    label:            d.opportunity_label             || problem.nova?.opportunity_label || '',
    marketShare:      d.market_share                  || [],
    growthTrend:      d.growth_trend                  || [],
    radar:            d.opportunity_radar             || [],
    competitors:      (d.competitors || []).map(c => ({
      name: c.name || '', rating: c.ios_rating || 0, reviews: c.ios_reviews || 0,
      price: c.ios_price || 'Free', fit: c.adhd_fit_score || 50,
      gap: c.gap_score || 50, weaknesses: c.weaknesses || [],
      website: c.website || '', pricing: c.pricing || {},
    })),
    fundingSummary:   d.funding?.funding_summary           || '—',
    totalInvested:    d.funding?.total_invested_in_space   || '—',
    recentRounds:     d.funding?.recent_rounds             || [],
    notableExits:     d.funding?.notable_exits             || [],
    investorInterest: d.funding?.investor_interest         || '—',
    rec:              d.recommendation                     || {},
    marketGap:        d.market_gap                         || '',
    winningStrategy:  d.winning_strategy                   || '',
    failurePatterns:  d.failure_patterns                   || [],
    graveyardIdeas:   d.graveyard_ideas                    || [],
    successFactors:   d.success_factors                    || [],
    marketSizeDetail: d.market_size                        || {},
    redditSignal:     d.reddit_signal                      || {},
  };

  const getSeverityColor = s => ({ Critical: 'bg-[#ef4444] text-white', High: 'bg-[#fbbf24] text-[#09090e]', Medium: 'bg-[#60a5fa] text-white' }[s] || 'bg-[#7b7a88] text-white');
  const getScoreColor    = s => s >= 85 ? '#4ade80' : s >= 70 ? '#fbbf24' : '#ff4500';
  const getVerdict       = s => s >= 85 ? { text: 'Build', color: '#4ade80' } : s >= 70 ? { text: 'Watch', color: '#fbbf24' } : { text: 'Pass', color: '#ef4444' };
  const formatNum        = n => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const verdict          = getVerdict(market.score);

  const buildSolutions = [
    ...(problem.nova?.solution_opportunities || []).map((s, i) => ({ id: i, title: s, type: 'App Idea', source: 'Nova Analysis' })),
    ...(market.rec?.build_first ? [{ id: 99, title: market.rec.build_first, type: 'Core Feature', source: 'Nova Recommendation' }] : []),
  ];

  return (
    <div className="min-h-screen bg-[#09090e] text-[#f0eff6]">

      {/* Generating overlay */}
      {generatingPrompt && <GeneratingOverlay solutionTitle={generatingSolution || ''} />}

      {/* ── Topbar ── */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-[#09090e]/95 border-b border-[#22222e] backdrop-blur-sm">
        <div className="flex items-center justify-between px-4 py-3">
          <button onClick={() => navigate('/dashboard')}
            className="text-[#7b7a88] hover:text-white transition-colors text-sm flex items-center gap-2"
            style={{ fontFamily: 'DM Mono, monospace' }} data-testid="back-to-problems-btn">
            ← Problems
          </button>
          <h1 className="text-sm font-bold truncate max-w-md" style={{ fontFamily: 'Syne, sans-serif' }}>
            {trunc(problem.reddit.title, 60)}
          </h1>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-[#22222e] rounded text-xs" style={{ color: categoryColor, fontFamily: 'DM Mono, monospace' }}>
              {problem.reddit.category}
            </span>
            <span className={`px-2 py-1 rounded text-xs font-bold ${getSeverityColor(problem.nova?.severity)}`} style={{ fontFamily: 'DM Mono, monospace' }}>
              {problem.nova?.severity}
            </span>
          </div>
        </div>
      </div>

      <div className="pt-16 px-4 pb-20">
        <div className="max-w-7xl mx-auto space-y-4">

          {/* ── Section 1: Problem Header ── */}
          <div className="bg-[#17171c] border border-[#22222e] rounded-xl p-5"
            style={{ borderLeft: `3px solid ${categoryColor}` }} data-testid="problem-header">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="px-2 py-0.5 bg-[#ff4500]/20 text-[#ff4500] rounded text-xs font-bold" style={{ fontFamily: 'DM Mono, monospace' }}>r/{problem.reddit.subreddit}</span>
              <span className={`px-2 py-0.5 rounded text-xs font-bold ${getSeverityColor(problem.nova?.severity)}`} style={{ fontFamily: 'DM Mono, monospace' }}>{problem.nova?.severity}</span>
              <span className="px-2 py-0.5 bg-[#22222e] text-[#7b7a88] rounded text-xs" style={{ fontFamily: 'DM Mono, monospace' }}>{problem.nova?.sentiment}</span>
              {problem.reddit.flair && <span className="px-2 py-0.5 bg-[#22222e] text-[#7b7a88] rounded text-xs" style={{ fontFamily: 'DM Mono, monospace' }}>{problem.reddit.flair}</span>}
            </div>
            <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>{problem.reddit.title}</h2>
            <div className="flex flex-wrap items-center gap-3 text-xs text-[#7b7a88] mb-3" style={{ fontFamily: 'DM Mono, monospace' }}>
              <span>u/{problem.reddit.author}</span>
              <span>⬆ {problem.reddit.score}</span>
              <span>💬 {problem.reddit.num_comments}</span>
              <span>{new Date(problem.reddit.posted_at).toLocaleDateString()}</span>
            </div>
            <a href={problem.reddit.url} target="_blank" rel="noopener noreferrer"
              className="text-[#ff4500] hover:underline text-xs" style={{ fontFamily: 'DM Mono, monospace' }}>
              View on Reddit ↗
            </a>
          </div>

          {/* ── Section 2: Post Body ── */}
          <div className="bg-[#17171c] border border-[#22222e] rounded-xl p-5" data-testid="post-body">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-[#7b7a88]" style={{ fontFamily: 'DM Mono, monospace' }}>POST BODY</span>
              <ViewBtn onClick={() => openModal('body')} />
            </div>
            <p className="text-[#7b7a88] text-sm leading-relaxed" style={{ fontFamily: 'DM Mono, monospace' }}>
              {trunc(problem.reddit.body, 200)}
            </p>
          </div>

          {/* ── Section 3: Nova Analysis ── */}
          <div data-testid="nova-analysis">
            <div className="flex items-center gap-2 mb-3">
              <span>🤖</span>
              <h3 className="text-lg font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>NOVA ANALYSIS</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-[#17171c] border border-[#22222e] rounded-xl p-5 flex flex-col">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-[#38bdf8]" style={{ fontFamily: 'DM Mono, monospace' }}>SUMMARY</span>
                  <ViewBtn onClick={() => openModal('summary')} />
                </div>
                <p className="text-[#7b7a88] text-xs leading-relaxed flex-1" style={{ fontFamily: 'DM Mono, monospace' }}>
                  {trunc(problem.nova?.problem_summary, 130)}
                </p>
              </div>

              <div className="bg-[#17171c] border border-[#22222e] rounded-xl p-5 flex flex-col border-l-2 border-l-[#ef4444]">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-[#ef4444]" style={{ fontFamily: 'DM Mono, monospace' }}>PAIN POINTS</span>
                  <ViewBtn onClick={() => openModal('pains')} />
                </div>
                <ul className="space-y-1 flex-1">
                  {(problem.nova?.key_pain_points || []).slice(0, 2).map((p, i) => (
                    <li key={i} className="flex gap-2 text-xs text-[#7b7a88]" style={{ fontFamily: 'DM Mono, monospace' }}>
                      <span className="text-[#ef4444] flex-shrink-0">{i + 1}.</span>
                      <span>{trunc(p, 70)}</span>
                    </li>
                  ))}
                  {(problem.nova?.key_pain_points || []).length > 2 &&
                    <li className="text-xs text-[#3a3a48]" style={{ fontFamily: 'DM Mono, monospace' }}>+{(problem.nova.key_pain_points.length - 2)} more</li>}
                </ul>
              </div>

              <div className="bg-[#17171c] border border-[#22222e] rounded-xl p-5 flex flex-col border-l-2 border-l-[#4ade80]">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-[#4ade80]" style={{ fontFamily: 'DM Mono, monospace' }}>OPPORTUNITIES</span>
                  <ViewBtn onClick={() => openModal('solutions')} />
                </div>
                <ul className="space-y-1 flex-1">
                  {(problem.nova?.solution_opportunities || []).slice(0, 2).map((s, i) => (
                    <li key={i} className="flex gap-2 text-xs text-[#7b7a88]" style={{ fontFamily: 'DM Mono, monospace' }}>
                      <span className="text-[#4ade80] flex-shrink-0">→</span>
                      <span>{trunc(s, 70)}</span>
                    </li>
                  ))}
                  {(problem.nova?.solution_opportunities || []).length > 2 &&
                    <li className="text-xs text-[#3a3a48]" style={{ fontFamily: 'DM Mono, monospace' }}>+{(problem.nova.solution_opportunities.length - 2)} more</li>}
                </ul>
              </div>
            </div>

            <div className="mt-3 flex items-center gap-3 bg-[#17171c] border border-[#22222e] rounded-xl px-5 py-3">
              <span className="text-xs font-bold text-[#38bdf8] flex-shrink-0" style={{ fontFamily: 'DM Mono, monospace' }}>AUDIENCE</span>
              <span className="text-xs text-[#7b7a88] flex-1" style={{ fontFamily: 'DM Mono, monospace' }}>{trunc(problem.nova?.affected_audience, 140)}</span>
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              {(problem.nova?.tags || []).map((tag, i) => (
                <span key={i} className="px-3 py-1 rounded-full text-xs font-medium"
                  style={{ backgroundColor: `${categoryColor}22`, color: categoryColor, fontFamily: 'DM Mono, monospace' }}>
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          {/* ── Section 4: Market Intelligence ── */}
          <div data-testid="market-intelligence">
            <div className="flex items-center gap-2 mb-3">
              <span>📊</span>
              <h3 className="text-lg font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>MARKET INTELLIGENCE</h3>
            </div>

            <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-4">
              {[
                { label: 'TAM',       value: market.tam,      color: '#38bdf8', testid: 'kpi-tam'   },
                { label: 'SAM',       value: market.sam,      color: '#a78bfa', testid: 'kpi-sam'   },
                { label: 'SOM',       value: market.som,      color: '#fb923c', testid: 'kpi-som'   },
                { label: 'CAGR',      value: market.cagr,     color: '#4ade80', testid: 'kpi-cagr'  },
                { label: 'POSTS/DAY', value: market.postsDay, color: '#ff4500', testid: 'kpi-posts' },
                { label: 'AVG UPVOTES',value:market.avgUpvotes,color:'#fbbf24', testid:'kpi-upvotes'},
              ].map(k => (
                <div key={k.testid} data-testid={k.testid}
                  className="bg-[#17171c] border border-[#22222e] rounded-xl p-4 text-center cursor-pointer hover:border-[#ff4500]/40 transition-all"
                  onClick={() => openModal('market')}>
                  <div className="text-xl font-bold" style={{ fontFamily: 'Syne, sans-serif', color: k.color }}>{k.value}</div>
                  <div className="text-[10px] text-[#7b7a88] mt-1" style={{ fontFamily: 'DM Mono, monospace' }}>{k.label}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
              {market.marketShare.length > 0 && (
                <div className="bg-[#17171c] border border-[#22222e] rounded-xl p-5" data-testid="market-share-chart">
                  <h4 className="text-sm font-bold mb-3" style={{ fontFamily: 'Syne, sans-serif' }}>Market Share</h4>
                  <PieChartComponent data={market.marketShare} />
                </div>
              )}
              {market.growthTrend.length > 0 && (
                <div className="bg-[#17171c] border border-[#22222e] rounded-xl p-5" data-testid="growth-trend-chart">
                  <h4 className="text-sm font-bold mb-3" style={{ fontFamily: 'Syne, sans-serif' }}>Growth Trend</h4>
                  <AreaChartComponent data={market.growthTrend} />
                </div>
              )}
              {market.competitors.length > 0 && (
                <div className="bg-[#17171c] border border-[#22222e] rounded-xl p-5" data-testid="competitor-chart">
                  <h4 className="text-sm font-bold mb-3" style={{ fontFamily: 'Syne, sans-serif' }}>Competitor Analysis</h4>
                  <BarChartComponent data={market.competitors} />
                </div>
              )}
              {market.radar.length > 0 && (
                <div className="bg-[#17171c] border border-[#22222e] rounded-xl p-5" data-testid="radar-chart">
                  <h4 className="text-sm font-bold mb-3" style={{ fontFamily: 'Syne, sans-serif' }}>Opportunity Radar</h4>
                  <RadarChartComponent data={market.radar} />
                </div>
              )}
            </div>

            {market.competitors.length > 0 && (
              <div className="bg-[#17171c] border border-[#22222e] rounded-xl p-5" data-testid="competitor-table">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>Competitors</h4>
                  <ViewBtn onClick={() => openModal('competitors')} />
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" style={{ fontFamily: 'DM Mono, monospace' }}>
                    <thead>
                      <tr className="border-b border-[#22222e]">
                        {['App', 'Rating', 'Reviews', 'Price', 'Fit', 'Gap'].map(h => (
                          <th key={h} className="text-left py-2 px-3 text-[#7b7a88] font-bold">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {market.competitors.slice(0, 3).map((c, i) => (
                        <tr key={i} className={i % 2 === 0 ? 'bg-[#1e1e26]/30' : ''}>
                          <td className="py-2 px-3 text-white">{trunc(c.name, 22)}</td>
                          <td className="py-2 px-3 text-[#fbbf24]">★ {c.rating}</td>
                          <td className="py-2 px-3 text-[#7b7a88]">{formatNum(c.reviews)}</td>
                          <td className="py-2 px-3 text-[#7b7a88]">{c.price}</td>
                          <td className="py-2 px-3">
                            <div className="flex items-center gap-1">
                              <div className="w-12 bg-[#22222e] rounded-full h-1.5">
                                <div className="h-full bg-[#ff4500] rounded-full" style={{ width: `${c.fit}%` }} />
                              </div>
                              <span className="text-white">{c.fit}%</span>
                            </div>
                          </td>
                          <td className="py-2 px-3" style={{ color: c.gap > 40 ? '#4ade80' : c.gap > 20 ? '#fbbf24' : '#ef4444' }}>
                            {c.gap}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {market.competitors.length > 3 &&
                    <p className="text-[10px] text-[#3a3a48] mt-2 px-3" style={{ fontFamily: 'DM Mono, monospace' }}>
                      +{market.competitors.length - 3} more — click VIEW for full table
                    </p>}
                </div>
              </div>
            )}
          </div>

          {/* ── Section 5: Funding + Recommendation ── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-[#17171c] border border-[#22222e] rounded-xl p-5 border-l-2 border-l-[#fbbf24]" data-testid="funding-card">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-[#fbbf24]" style={{ fontFamily: 'DM Mono, monospace' }}>💰 FUNDING</span>
                <ViewBtn onClick={() => openModal('funding')} />
              </div>
              <p className="text-white text-sm font-bold mb-1" style={{ fontFamily: 'DM Mono, monospace' }}>{market.totalInvested} total invested</p>
              <p className="text-[#7b7a88] text-xs" style={{ fontFamily: 'DM Mono, monospace' }}>{trunc(market.fundingSummary, 100)}</p>
            </div>

            <div className="bg-[#17171c] border border-[#22222e] rounded-xl p-5 border-l-2 border-l-[#4ade80]" data-testid="recommendation-card">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-[#4ade80]" style={{ fontFamily: 'DM Mono, monospace' }}>✓ RECOMMENDATION</span>
                <ViewBtn onClick={() => openModal('recommendation')} />
              </div>
              <p className="text-white text-sm font-bold mb-1" style={{ fontFamily: 'DM Mono, monospace' }}>Verdict: {market.rec?.verdict || '—'}</p>
              <p className="text-[#7b7a88] text-xs" style={{ fontFamily: 'DM Mono, monospace' }}>{trunc(market.rec?.summary, 100)}</p>
            </div>
          </div>

          {/* ── Section 6: Opportunity Score + Failure Patterns ── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-[#17171c] border border-[#22222e] rounded-xl p-5 flex flex-col items-center justify-center" data-testid="opportunity-score">
              <h4 className="text-sm font-bold mb-1" style={{ fontFamily: 'Syne, sans-serif' }}>OPPORTUNITY SCORE</h4>
              <p className="text-[#7b7a88] text-xs mb-4" style={{ fontFamily: 'DM Mono, monospace' }}>{market.label}</p>
              <div className="relative w-36 h-36">
                <svg className="w-36 h-36 transform -rotate-90">
                  <circle cx="72" cy="72" r="60" stroke="#22222e" strokeWidth="10" fill="none" />
                  <circle cx="72" cy="72" r="60"
                    stroke={getScoreColor(market.score)} strokeWidth="10" fill="none"
                    strokeDasharray={`${2 * Math.PI * 60}`}
                    strokeDashoffset={`${2 * Math.PI * 60 * (1 - scoreProgress / 100)}`}
                    strokeLinecap="round" className="transition-all duration-1000" />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center flex-col">
                  <span className="text-4xl font-bold" style={{ fontFamily: 'Syne, sans-serif', color: getScoreColor(market.score) }}>
                    {Math.round(scoreProgress)}
                  </span>
                  <span className="text-[#7b7a88] text-xs" style={{ fontFamily: 'DM Mono, monospace' }}>/100</span>
                </div>
              </div>
            </div>

            {market.failurePatterns.length > 0 && (
              <div className="bg-[#17171c] border border-[#22222e] rounded-xl p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold text-[#ef4444]" style={{ fontFamily: 'DM Mono, monospace' }}>⚠️ FAILURE PATTERNS</span>
                  <ViewBtn onClick={() => openModal('failure')} />
                </div>
                <ul className="space-y-2">
                  {market.failurePatterns.slice(0, 3).map((f, i) => (
                    <li key={i} className="text-xs" style={{ fontFamily: 'DM Mono, monospace' }}>
                      <span className="text-white font-bold">{f.pattern}</span>
                      <span className="text-[#7b7a88] ml-2">— {trunc(f.description, 55)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* ── Section 7: Build CTA ── */}
          <div className="bg-[#17171c] border border-[#22222e] rounded-xl p-6 border-l-4 border-l-[#ff4500]" data-testid="build-startup-section">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="flex-1">
                <p className="text-[#7b7a88] text-xs mb-1" style={{ fontFamily: 'DM Mono, monospace' }}>NOVA VERDICT</p>
                <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Syne, sans-serif', color: verdict.color }}>
                  {verdict.text} this opportunity.
                </h3>
                <p className="text-[#7b7a88] text-xs" style={{ fontFamily: 'DM Mono, monospace' }}>
                  {market.competitors.length} competitors · {market.tam} TAM · {market.postsDay}/day Reddit signal
                </p>
              </div>
              <button
                onClick={() => openModal('build')}
                className="flex-shrink-0 px-8 py-4 bg-[#ff4500] text-white rounded-xl hover:bg-[#e03d00] hover:scale-105 transition-all duration-300 font-bold text-lg flex items-center gap-3"
                style={{ fontFamily: 'Syne, sans-serif' }}
                data-testid="build-startup-btn">
                🚀 Build This Startup →
              </button>
            </div>
          </div>

        </div>
      </div>

      {/* ════ MODALS ════ */}

      <Modal isOpen={modal === 'body'} onClose={closeModal} title="Full Post">
        <p className="text-[#7b7a88] leading-relaxed text-sm whitespace-pre-wrap" style={{ fontFamily: 'DM Mono, monospace' }}>
          {problem.reddit.body}
        </p>
      </Modal>

      <Modal isOpen={modal === 'summary'} onClose={closeModal} title="Problem Summary">
        <p className="text-white leading-relaxed text-sm" style={{ fontFamily: 'DM Mono, monospace' }}>
          {problem.nova?.problem_summary}
        </p>
        {problem.nova?.affected_audience && (
          <div className="mt-4 pt-4 border-t border-[#22222e]">
            <p className="text-[#38bdf8] text-xs font-bold mb-1" style={{ fontFamily: 'DM Mono, monospace' }}>AFFECTED AUDIENCE</p>
            <p className="text-[#7b7a88] text-sm" style={{ fontFamily: 'DM Mono, monospace' }}>{problem.nova.affected_audience}</p>
          </div>
        )}
      </Modal>

      <Modal isOpen={modal === 'pains'} onClose={closeModal} title="Key Pain Points">
        <ol className="space-y-4">
          {(problem.nova?.key_pain_points || []).map((p, i) => (
            <li key={i} className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#ef4444] text-white flex items-center justify-center text-xs font-bold">{i + 1}</span>
              <span className="text-[#7b7a88] text-sm leading-relaxed" style={{ fontFamily: 'DM Mono, monospace' }}>{p}</span>
            </li>
          ))}
        </ol>
      </Modal>

      <Modal isOpen={modal === 'solutions'} onClose={closeModal} title="Solution Opportunities">
        <ul className="space-y-4">
          {(problem.nova?.solution_opportunities || []).map((s, i) => (
            <li key={i} className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#4ade80] text-[#09090e] flex items-center justify-center text-xs font-bold">→</span>
              <span className="text-[#7b7a88] text-sm leading-relaxed" style={{ fontFamily: 'DM Mono, monospace' }}>{s}</span>
            </li>
          ))}
        </ul>
      </Modal>

      <Modal isOpen={modal === 'market'} onClose={closeModal} title="Market Size Detail">
        {[
          { label: 'TAM — Total Addressable Market', value: market.tam, desc: market.marketSizeDetail.tam_description, reason: market.marketSizeDetail.tam_reasoning, color: '#38bdf8' },
          { label: 'SAM — Serviceable Available Market', value: market.sam, desc: market.marketSizeDetail.sam_description, reason: market.marketSizeDetail.sam_reasoning, color: '#a78bfa' },
          { label: 'SOM — Serviceable Obtainable Market', value: market.som, desc: market.marketSizeDetail.som_description, reason: market.marketSizeDetail.som_reasoning, color: '#fb923c' },
          { label: 'CAGR — Compound Annual Growth Rate', value: market.cagr, desc: market.marketSizeDetail.cagr_description, color: '#4ade80' },
        ].map(item => (
          <div key={item.label} className="mb-5 pb-5 border-b border-[#22222e] last:border-0">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-2xl font-bold" style={{ fontFamily: 'Syne, sans-serif', color: item.color }}>{item.value}</span>
              <span className="text-xs text-[#7b7a88]" style={{ fontFamily: 'DM Mono, monospace' }}>{item.label}</span>
            </div>
            {item.desc && <p className="text-sm text-[#7b7a88] mb-1" style={{ fontFamily: 'DM Mono, monospace' }}>{item.desc}</p>}
            {item.reason && <p className="text-xs text-[#3a3a48]" style={{ fontFamily: 'DM Mono, monospace' }}>Reasoning: {item.reason}</p>}
          </div>
        ))}
      </Modal>

      <Modal isOpen={modal === 'competitors'} onClose={closeModal} title="Full Competitor Analysis">
        <div className="space-y-4">
          {market.competitors.map((c, i) => (
            <div key={i} className="bg-[#09090e] rounded-xl p-4 border border-[#22222e]">
              <div className="flex items-center justify-between mb-2">
                <span className="font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>{c.name}</span>
                <div className="flex items-center gap-2">
                  <span className="text-[#fbbf24] text-sm">★ {c.rating}</span>
                  <span className="text-xs text-[#7b7a88]" style={{ fontFamily: 'DM Mono, monospace' }}>{formatNum(c.reviews)} reviews</span>
                  <span className="px-2 py-0.5 bg-[#22222e] text-[#7b7a88] rounded text-xs" style={{ fontFamily: 'DM Mono, monospace' }}>{c.price}</span>
                </div>
              </div>
              <div className="flex items-center gap-3 mb-2">
                <span className="text-xs text-[#7b7a88]" style={{ fontFamily: 'DM Mono, monospace' }}>Audience Fit</span>
                <div className="flex-1 bg-[#22222e] rounded-full h-2">
                  <div className="h-full bg-[#ff4500] rounded-full" style={{ width: `${c.fit}%` }} />
                </div>
                <span className="text-white text-xs font-bold" style={{ fontFamily: 'DM Mono, monospace' }}>{c.fit}%</span>
              </div>
              {c.weaknesses?.length > 0 && (
                <ul className="space-y-1">
                  {c.weaknesses.map((w, j) => (
                    <li key={j} className="text-xs text-[#7b7a88] flex gap-2" style={{ fontFamily: 'DM Mono, monospace' }}>
                      <span className="text-[#ef4444]">✗</span>{w}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
          {market.marketGap && (
            <div className="mt-4 p-4 bg-[#4ade80]/10 border border-[#4ade80]/20 rounded-xl">
              <p className="text-xs font-bold text-[#4ade80] mb-1" style={{ fontFamily: 'DM Mono, monospace' }}>MARKET GAP</p>
              <p className="text-sm text-[#7b7a88]" style={{ fontFamily: 'DM Mono, monospace' }}>{market.marketGap}</p>
            </div>
          )}
        </div>
      </Modal>

      <Modal isOpen={modal === 'funding'} onClose={closeModal} title="Funding Intelligence">
        <div className="grid grid-cols-3 gap-3 mb-5">
          <div className="bg-[#09090e] rounded-xl p-3 text-center">
            <p className="text-lg font-bold text-[#fbbf24]" style={{ fontFamily: 'Syne, sans-serif' }}>{market.totalInvested}</p>
            <p className="text-[10px] text-[#7b7a88]" style={{ fontFamily: 'DM Mono, monospace' }}>TOTAL INVESTED</p>
          </div>
          <div className="bg-[#09090e] rounded-xl p-3 text-center">
            <p className="text-lg font-bold text-[#4ade80]" style={{ fontFamily: 'Syne, sans-serif' }}>{market.investorInterest}</p>
            <p className="text-[10px] text-[#7b7a88]" style={{ fontFamily: 'DM Mono, monospace' }}>INVESTOR INTEREST</p>
          </div>
          <div className="bg-[#09090e] rounded-xl p-3 text-center">
            <p className="text-lg font-bold text-[#60a5fa]" style={{ fontFamily: 'Syne, sans-serif' }}>{market.recentRounds.length}</p>
            <p className="text-[10px] text-[#7b7a88]" style={{ fontFamily: 'DM Mono, monospace' }}>RECENT ROUNDS</p>
          </div>
        </div>
        {market.recentRounds.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-bold text-[#7b7a88] mb-2" style={{ fontFamily: 'DM Mono, monospace' }}>RECENT ROUNDS</p>
            <div className="space-y-2">
              {market.recentRounds.map((r, i) => (
                <div key={i} className="flex items-center justify-between bg-[#09090e] rounded-lg p-3">
                  <div>
                    <span className="text-white text-sm font-bold" style={{ fontFamily: 'DM Mono, monospace' }}>{r.company}</span>
                    <span className="text-[#7b7a88] text-xs ml-2">{r.round} · {r.date}</span>
                  </div>
                  <span className="text-[#fbbf24] font-bold text-sm" style={{ fontFamily: 'DM Mono, monospace' }}>{r.amount}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <p className="text-[#7b7a88] text-sm mt-4 leading-relaxed" style={{ fontFamily: 'DM Mono, monospace' }}>{market.fundingSummary}</p>
      </Modal>

      <Modal isOpen={modal === 'recommendation'} onClose={closeModal} title="Nova Recommendation">
        {market.rec && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <span className="text-3xl font-bold" style={{ fontFamily: 'Syne, sans-serif', color: verdict.color }}>{market.rec.verdict}</span>
              <span className="px-3 py-1 bg-[#22222e] rounded text-sm text-[#7b7a88]" style={{ fontFamily: 'DM Mono, monospace' }}>Confidence: {market.rec.confidence}</span>
            </div>
            {[
              { label: 'SUMMARY',        value: market.rec.summary,          color: '#f0eff6' },
              { label: 'KEY INSIGHT',    value: market.rec.key_insight,      color: '#38bdf8' },
              { label: 'TARGET FIRST',   value: market.rec.target_first,     color: '#4ade80' },
              { label: 'BUILD FIRST',    value: market.rec.build_first,      color: '#a78bfa' },
              { label: 'PRICING',        value: market.rec.pricing_insight,  color: '#fbbf24' },
              { label: 'GO TO MARKET',   value: market.rec.go_to_market,     color: '#fb923c' },
              { label: 'TIMELINE',       value: market.rec.timeline,         color: '#60a5fa' },
              { label: 'AVOID',          value: market.rec.avoid,            color: '#ef4444' },
            ].filter(item => item.value).map(item => (
              <div key={item.label} className="border-t border-[#22222e] pt-3">
                <p className="text-xs font-bold mb-1" style={{ fontFamily: 'DM Mono, monospace', color: item.color }}>{item.label}</p>
                <p className="text-sm text-[#7b7a88] leading-relaxed" style={{ fontFamily: 'DM Mono, monospace' }}>{item.value}</p>
              </div>
            ))}
            {market.rec.risks?.length > 0 && (
              <div className="border-t border-[#22222e] pt-3">
                <p className="text-xs font-bold text-[#ef4444] mb-2" style={{ fontFamily: 'DM Mono, monospace' }}>RISKS</p>
                <ul className="space-y-1">
                  {market.rec.risks.map((r, i) => (
                    <li key={i} className="text-xs text-[#7b7a88] flex gap-2" style={{ fontFamily: 'DM Mono, monospace' }}>
                      <span className="text-[#ef4444]">!</span>{r}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal isOpen={modal === 'failure'} onClose={closeModal} title="Failure Patterns & Lessons">
        <div className="space-y-5">
          {market.failurePatterns.map((f, i) => (
            <div key={i} className="bg-[#09090e] rounded-xl p-4">
              <p className="text-white font-bold text-sm mb-1" style={{ fontFamily: 'Syne, sans-serif' }}>{f.pattern}</p>
              <p className="text-[#7b7a88] text-xs mb-2 leading-relaxed" style={{ fontFamily: 'DM Mono, monospace' }}>{f.description}</p>
              {f.examples?.length > 0 && (
                <ul className="space-y-1">
                  {f.examples.map((e, j) => (
                    <li key={j} className="text-xs text-[#3a3a48] flex gap-2" style={{ fontFamily: 'DM Mono, monospace' }}>
                      <span className="text-[#ef4444]">eg.</span>{e}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
          {market.successFactors.length > 0 && (
            <div>
              <p className="text-xs font-bold text-[#4ade80] mb-2" style={{ fontFamily: 'DM Mono, monospace' }}>SUCCESS FACTORS</p>
              <ul className="space-y-1">
                {market.successFactors.map((s, i) => (
                  <li key={i} className="text-xs text-[#7b7a88] flex gap-2" style={{ fontFamily: 'DM Mono, monospace' }}>
                    <span className="text-[#4ade80]">✓</span>{s}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {market.graveyardIdeas.length > 0 && (
            <div className="mt-4 p-4 bg-[#ef4444]/10 border border-[#ef4444]/20 rounded-xl">
              <p className="text-xs font-bold text-[#ef4444] mb-2" style={{ fontFamily: 'DM Mono, monospace' }}>GRAVEYARD IDEAS (tried & failed)</p>
              <ul className="space-y-1">
                {market.graveyardIdeas.map((g, i) => (
                  <li key={i} className="text-xs text-[#7b7a88] flex gap-2" style={{ fontFamily: 'DM Mono, monospace' }}>
                    <span className="text-[#ef4444]">✗</span>{g}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Modal>

      {/* ── Build This Startup Modal — now with clickable solutions ── */}
      <Modal isOpen={modal === 'build'} onClose={closeModal} title="Build This Startup">
        <div className="mb-5 p-4 bg-[#ff4500]/10 border border-[#ff4500]/20 rounded-xl">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-2xl font-bold" style={{ fontFamily: 'Syne, sans-serif', color: verdict.color }}>{verdict.text}</span>
            <span className="text-[#7b7a88] text-xs" style={{ fontFamily: 'DM Mono, monospace' }}>
              {market.competitors.length} competitors · {market.tam} TAM · {market.cagr} CAGR
            </span>
          </div>
          {market.rec?.summary && (
            <p className="text-[#7b7a88] text-xs leading-relaxed" style={{ fontFamily: 'DM Mono, monospace' }}>{market.rec.summary}</p>
          )}
        </div>

        {/* Instruction banner */}
        <div className="mb-4 px-4 py-2.5 bg-[#ff4500]/5 border border-[#ff4500]/20 rounded-lg flex items-center gap-2">
          <span className="text-[#ff4500]">⚡</span>
          <p className="text-[#ff4500] text-xs font-bold" style={{ fontFamily: 'DM Mono, monospace' }}>
            Click any solution below — Nova will generate the full app for you
          </p>
        </div>

        <p className="text-xs font-bold text-[#7b7a88] mb-3" style={{ fontFamily: 'DM Mono, monospace' }}>
          SOLUTION IDEAS TO BUILD ({buildSolutions.length})
        </p>

        <div className="space-y-3">
          {buildSolutions.map((sol, i) => (
            <button
              key={sol.id}
              onClick={() => {
                closeModal();
                handleBuildSolution(sol);
              }}
              className="w-full bg-[#09090e] border border-[#22222e] hover:border-[#ff4500]/60 hover:bg-[#ff4500]/5 rounded-xl p-4 transition-all cursor-pointer group text-left"
              style={{ outline: 'none' }}>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-[#ff4500]/20 group-hover:bg-[#ff4500]/30 flex items-center justify-center text-sm font-bold text-[#ff4500] transition-all"
                  style={{ fontFamily: 'Syne, sans-serif' }}>
                  {i + 1}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold"
                      style={{ fontFamily: 'DM Mono, monospace',
                        backgroundColor: sol.type === 'Core Feature' ? '#a78bfa22' : '#38bdf822',
                        color: sol.type === 'Core Feature' ? '#a78bfa' : '#38bdf8' }}>
                      {sol.type}
                    </span>
                    <span className="text-[10px] text-[#3a3a48]" style={{ fontFamily: 'DM Mono, monospace' }}>{sol.source}</span>
                  </div>
                  <p className="text-white text-sm leading-relaxed group-hover:text-[#f0eff6]"
                    style={{ fontFamily: 'DM Mono, monospace' }}>{sol.title}</p>
                </div>
                {/* Arrow indicator */}
                <div className="flex-shrink-0 text-[#ff4500] opacity-0 group-hover:opacity-100 transition-all text-lg">→</div>
              </div>
              {/* Hover CTA */}
              <div className="mt-2 ml-11 opacity-0 group-hover:opacity-100 transition-all">
                <span className="text-[10px] text-[#ff4500] font-bold" style={{ fontFamily: 'DM Mono, monospace' }}>
                  🚀 Click to generate app with Nova →
                </span>
              </div>
            </button>
          ))}
        </div>

        {market.winningStrategy && (
          <div className="mt-5 p-4 bg-[#4ade80]/10 border border-[#4ade80]/20 rounded-xl">
            <p className="text-xs font-bold text-[#4ade80] mb-1" style={{ fontFamily: 'DM Mono, monospace' }}>WINNING STRATEGY</p>
            <p className="text-sm text-[#7b7a88] leading-relaxed" style={{ fontFamily: 'DM Mono, monospace' }}>{market.winningStrategy}</p>
          </div>
        )}

        {market.rec?.go_to_market && (
          <div className="mt-3 p-4 bg-[#38bdf8]/10 border border-[#38bdf8]/20 rounded-xl">
            <p className="text-xs font-bold text-[#38bdf8] mb-1" style={{ fontFamily: 'DM Mono, monospace' }}>GO-TO-MARKET</p>
            <p className="text-sm text-[#7b7a88] leading-relaxed" style={{ fontFamily: 'DM Mono, monospace' }}>{market.rec.go_to_market}</p>
          </div>
        )}
      </Modal>

    </div>
  );
};

export default ProblemDetail;